/* __next_internal_client_entry_do_not_use__ default auto */ import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import composeClasses from '@mui/utils/composeClasses';
import { darken, lighten } from '@mui/system/colorManipulator';
import { styled } from "../zero-styled/index.js";
import memoTheme from "../utils/memoTheme.js";
import { useDefaultProps } from "../DefaultPropsProvider/index.js";
import useSlot from "../utils/useSlot.js";
import capitalize from "../utils/capitalize.js";
import createSimplePaletteValueFilter from "../utils/createSimplePaletteValueFilter.js";
import Paper from "../Paper/index.js";
import alertClasses, { getAlertUtilityClass } from "./alertClasses.js";
import IconButton from "../IconButton/index.js";
import SuccessOutlinedIcon from "../internal/svg-icons/SuccessOutlined.js";
import ReportProblemOutlinedIcon from "../internal/svg-icons/ReportProblemOutlined.js";
import ErrorOutlineIcon from "../internal/svg-icons/ErrorOutline.js";
import InfoOutlinedIcon from "../internal/svg-icons/InfoOutlined.js";
import CloseIcon from "../internal/svg-icons/Close.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const useUtilityClasses = (ownerState)=>{
    const { variant, color, severity, classes } = ownerState;
    const slots = {
        root: [
            'root',
            "color".concat(capitalize(color || severity)),
            "".concat(variant).concat(capitalize(color || severity)),
            "".concat(variant)
        ],
        icon: [
            'icon'
        ],
        message: [
            'message'
        ],
        action: [
            'action'
        ]
    };
    return composeClasses(slots, getAlertUtilityClass, classes);
};
const AlertRoot = styled(Paper, {
    name: 'MuiAlert',
    slot: 'Root',
    overridesResolver: (props, styles)=>{
        const { ownerState } = props;
        return [
            styles.root,
            styles[ownerState.variant],
            styles["".concat(ownerState.variant).concat(capitalize(ownerState.color || ownerState.severity))]
        ];
    }
})(memoTheme((param)=>{
    let { theme } = param;
    const getColor = theme.palette.mode === 'light' ? darken : lighten;
    const getBackgroundColor = theme.palette.mode === 'light' ? lighten : darken;
    return {
        ...theme.typography.body2,
        backgroundColor: 'transparent',
        display: 'flex',
        padding: '6px 16px',
        variants: [
            ...Object.entries(theme.palette).filter(createSimplePaletteValueFilter([
                'light'
            ])).map((param)=>{
                let [color] = param;
                return {
                    props: {
                        colorSeverity: color,
                        variant: 'standard'
                    },
                    style: {
                        color: theme.vars ? theme.vars.palette.Alert["".concat(color, "Color")] : getColor(theme.palette[color].light, 0.6),
                        backgroundColor: theme.vars ? theme.vars.palette.Alert["".concat(color, "StandardBg")] : getBackgroundColor(theme.palette[color].light, 0.9),
                        ["& .".concat(alertClasses.icon)]: theme.vars ? {
                            color: theme.vars.palette.Alert["".concat(color, "IconColor")]
                        } : {
                            color: theme.palette[color].main
                        }
                    }
                };
            }),
            ...Object.entries(theme.palette).filter(createSimplePaletteValueFilter([
                'light'
            ])).map((param)=>{
                let [color] = param;
                return {
                    props: {
                        colorSeverity: color,
                        variant: 'outlined'
                    },
                    style: {
                        color: theme.vars ? theme.vars.palette.Alert["".concat(color, "Color")] : getColor(theme.palette[color].light, 0.6),
                        border: "1px solid ".concat((theme.vars || theme).palette[color].light),
                        ["& .".concat(alertClasses.icon)]: theme.vars ? {
                            color: theme.vars.palette.Alert["".concat(color, "IconColor")]
                        } : {
                            color: theme.palette[color].main
                        }
                    }
                };
            }),
            ...Object.entries(theme.palette).filter(createSimplePaletteValueFilter([
                'dark'
            ])).map((param)=>{
                let [color] = param;
                return {
                    props: {
                        colorSeverity: color,
                        variant: 'filled'
                    },
                    style: {
                        fontWeight: theme.typography.fontWeightMedium,
                        ...theme.vars ? {
                            color: theme.vars.palette.Alert["".concat(color, "FilledColor")],
                            backgroundColor: theme.vars.palette.Alert["".concat(color, "FilledBg")]
                        } : {
                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette[color].dark : theme.palette[color].main,
                            color: theme.palette.getContrastText(theme.palette[color].main)
                        }
                    }
                };
            })
        ]
    };
}));
const AlertIcon = styled('div', {
    name: 'MuiAlert',
    slot: 'Icon',
    overridesResolver: (props, styles)=>styles.icon
})({
    marginRight: 12,
    padding: '7px 0',
    display: 'flex',
    fontSize: 22,
    opacity: 0.9
});
const AlertMessage = styled('div', {
    name: 'MuiAlert',
    slot: 'Message',
    overridesResolver: (props, styles)=>styles.message
})({
    padding: '8px 0',
    minWidth: 0,
    overflow: 'auto'
});
const AlertAction = styled('div', {
    name: 'MuiAlert',
    slot: 'Action',
    overridesResolver: (props, styles)=>styles.action
})({
    display: 'flex',
    alignItems: 'flex-start',
    padding: '4px 0 0 16px',
    marginLeft: 'auto',
    marginRight: -8
});
const defaultIconMapping = {
    success: /*#__PURE__*/ _jsx(SuccessOutlinedIcon, {
        fontSize: "inherit"
    }),
    warning: /*#__PURE__*/ _jsx(ReportProblemOutlinedIcon, {
        fontSize: "inherit"
    }),
    error: /*#__PURE__*/ _jsx(ErrorOutlineIcon, {
        fontSize: "inherit"
    }),
    info: /*#__PURE__*/ _jsx(InfoOutlinedIcon, {
        fontSize: "inherit"
    })
};
const Alert = /*#__PURE__*/ React.forwardRef(function Alert(inProps, ref) {
    const props = useDefaultProps({
        props: inProps,
        name: 'MuiAlert'
    });
    const { action, children, className, closeText = 'Close', color, components = {}, componentsProps = {}, icon, iconMapping = defaultIconMapping, onClose, role = 'alert', severity = 'success', slotProps = {}, slots = {}, variant = 'standard', ...other } = props;
    const ownerState = {
        ...props,
        color,
        severity,
        variant,
        colorSeverity: color || severity
    };
    const classes = useUtilityClasses(ownerState);
    const externalForwardedProps = {
        slots: {
            closeButton: components.CloseButton,
            closeIcon: components.CloseIcon,
            ...slots
        },
        slotProps: {
            ...componentsProps,
            ...slotProps
        }
    };
    const [RootSlot, rootSlotProps] = useSlot('root', {
        ref,
        shouldForwardComponentProp: true,
        className: clsx(classes.root, className),
        elementType: AlertRoot,
        externalForwardedProps: {
            ...externalForwardedProps,
            ...other
        },
        ownerState,
        additionalProps: {
            role,
            elevation: 0
        }
    });
    const [IconSlot, iconSlotProps] = useSlot('icon', {
        className: classes.icon,
        elementType: AlertIcon,
        externalForwardedProps,
        ownerState
    });
    const [MessageSlot, messageSlotProps] = useSlot('message', {
        className: classes.message,
        elementType: AlertMessage,
        externalForwardedProps,
        ownerState
    });
    const [ActionSlot, actionSlotProps] = useSlot('action', {
        className: classes.action,
        elementType: AlertAction,
        externalForwardedProps,
        ownerState
    });
    const [CloseButtonSlot, closeButtonProps] = useSlot('closeButton', {
        elementType: IconButton,
        externalForwardedProps,
        ownerState
    });
    const [CloseIconSlot, closeIconProps] = useSlot('closeIcon', {
        elementType: CloseIcon,
        externalForwardedProps,
        ownerState
    });
    return /*#__PURE__*/ _jsxs(RootSlot, {
        ...rootSlotProps,
        children: [
            icon !== false ? /*#__PURE__*/ _jsx(IconSlot, {
                ...iconSlotProps,
                children: icon || iconMapping[severity] || defaultIconMapping[severity]
            }) : null,
            /*#__PURE__*/ _jsx(MessageSlot, {
                ...messageSlotProps,
                children: children
            }),
            action != null ? /*#__PURE__*/ _jsx(ActionSlot, {
                ...actionSlotProps,
                children: action
            }) : null,
            action == null && onClose ? /*#__PURE__*/ _jsx(ActionSlot, {
                ...actionSlotProps,
                children: /*#__PURE__*/ _jsx(CloseButtonSlot, {
                    size: "small",
                    "aria-label": closeText,
                    title: closeText,
                    color: "inherit",
                    onClick: onClose,
                    ...closeButtonProps,
                    children: /*#__PURE__*/ _jsx(CloseIconSlot, {
                        fontSize: "small",
                        ...closeIconProps
                    })
                })
            }) : null
        ]
    });
});
"production" !== "production" ? Alert.propTypes = {
    // ┌────────────────────────────── Warning ──────────────────────────────┐
    // │ These PropTypes are generated from the TypeScript type definitions. │
    // │    To update them, edit the d.ts file and run `pnpm proptypes`.     │
    // └─────────────────────────────────────────────────────────────────────┘
    /**
   * The action to display. It renders after the message, at the end of the alert.
   */ action: PropTypes.node,
    /**
   * The content of the component.
   */ children: PropTypes.node,
    /**
   * Override or extend the styles applied to the component.
   */ classes: PropTypes.object,
    /**
   * @ignore
   */ className: PropTypes.string,
    /**
   * Override the default label for the *close popup* icon button.
   *
   * For localization purposes, you can use the provided [translations](https://mui.com/material-ui/guides/localization/).
   * @default 'Close'
   */ closeText: PropTypes.string,
    /**
   * The color of the component. Unless provided, the value is taken from the `severity` prop.
   * It supports both default and custom theme colors, which can be added as shown in the
   * [palette customization guide](https://mui.com/material-ui/customization/palette/#custom-colors).
   */ color: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'error',
            'info',
            'success',
            'warning'
        ]),
        PropTypes.string
    ]),
    /**
   * The components used for each slot inside.
   *
   * @deprecated use the `slots` prop instead. This prop will be removed in v7. See [Migrating from deprecated APIs](https://mui.com/material-ui/migration/migrating-from-deprecated-apis/) for more details.
   *
   * @default {}
   */ components: PropTypes.shape({
        CloseButton: PropTypes.elementType,
        CloseIcon: PropTypes.elementType
    }),
    /**
   * The extra props for the slot components.
   * You can override the existing props or add new ones.
   *
   * @deprecated use the `slotProps` prop instead. This prop will be removed in v7. See [Migrating from deprecated APIs](https://mui.com/material-ui/migration/migrating-from-deprecated-apis/) for more details.
   *
   * @default {}
   */ componentsProps: PropTypes.shape({
        closeButton: PropTypes.object,
        closeIcon: PropTypes.object
    }),
    /**
   * Override the icon displayed before the children.
   * Unless provided, the icon is mapped to the value of the `severity` prop.
   * Set to `false` to remove the `icon`.
   */ icon: PropTypes.node,
    /**
   * The component maps the `severity` prop to a range of different icons,
   * for instance success to `<SuccessOutlined>`.
   * If you wish to change this mapping, you can provide your own.
   * Alternatively, you can use the `icon` prop to override the icon displayed.
   */ iconMapping: PropTypes.shape({
        error: PropTypes.node,
        info: PropTypes.node,
        success: PropTypes.node,
        warning: PropTypes.node
    }),
    /**
   * Callback fired when the component requests to be closed.
   * When provided and no `action` prop is set, a close icon button is displayed that triggers the callback when clicked.
   * @param {React.SyntheticEvent} event The event source of the callback.
   */ onClose: PropTypes.func,
    /**
   * The ARIA role attribute of the element.
   * @default 'alert'
   */ role: PropTypes.string,
    /**
   * The severity of the alert. This defines the color and icon used.
   * @default 'success'
   */ severity: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'error',
            'info',
            'success',
            'warning'
        ]),
        PropTypes.string
    ]),
    /**
   * The props used for each slot inside.
   * @default {}
   */ slotProps: PropTypes.shape({
        action: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ]),
        closeButton: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ]),
        closeIcon: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ]),
        icon: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ]),
        message: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ]),
        root: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ])
    }),
    /**
   * The components used for each slot inside.
   * @default {}
   */ slots: PropTypes.shape({
        action: PropTypes.elementType,
        closeButton: PropTypes.elementType,
        closeIcon: PropTypes.elementType,
        icon: PropTypes.elementType,
        message: PropTypes.elementType,
        root: PropTypes.elementType
    }),
    /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */ sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object,
            PropTypes.bool
        ])),
        PropTypes.func,
        PropTypes.object
    ]),
    /**
   * The variant to use.
   * @default 'standard'
   */ variant: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'filled',
            'outlined',
            'standard'
        ]),
        PropTypes.string
    ])
} : void 0;
export default Alert;
